import { APPOINTMENT_TYPE_SELECTION_EVENTS, DICT_DEFAULT_RESOURCE_TEXT } from "../utils/constants";
import { getNextFocusIndex } from "../utils/keyboard";

export default class AppointmentTypeComponent extends HTMLElement {
  #currentActiveId = null;
  #resourcesService;

  constructor() {
    super();
    this.setAttribute("role", "radiogroup");
    this.setAttribute("tabindex", "0");
    
    this.addEventListener("keydown", this.#handleKeyDown.bind(this));
    this.addEventListener("focus", () => {
      // When container gets focus, ensure we have an active descendant
      if (!this.#currentActiveId) {
        const firstButton = this.querySelector("button");
        if (firstButton) {
          this.#setActiveDescendant(firstButton.id);
        }
      }
    });
  }

  init(resourcesService) {
    this.#resourcesService = resourcesService;
    this.setAttribute("aria-label", 
      this.#resourcesService?.getResource("SELFSCHEDULER_APPOINTMENTTYPE_ARIA_LABEL", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_APPOINTMENTTYPE_ARIA_LABEL)
    );
  }

  #handleKeyDown(event) {
    const buttons = Array.from(this.querySelectorAll("button"));
    if (!buttons.length) return;

    const currentIndex = buttons.findIndex(btn => btn.id === this.#currentActiveId);
    let newIndex = currentIndex;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        newIndex = currentIndex + 1;
        if (newIndex >= buttons.length) newIndex = 0;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = buttons.length - 1;
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (this.#currentActiveId) {
          const activeButton = this.querySelector(`#${this.#currentActiveId}`);
          if (activeButton) {
            activeButton.click();
          }
        }
        return;
    }

    if (newIndex !== currentIndex) {
      this.#setActiveDescendant(buttons[newIndex].id);
    }
  }

  #setActiveDescendant(buttonId) {
    // Remove previous active state
    if (this.#currentActiveId) {
      const prevButton = this.querySelector(`#${this.#currentActiveId}`);
      if (prevButton) {
        prevButton.classList.remove("v24-active-apt-type");
        prevButton.setAttribute("aria-selected", "false");
      }
    }

    // Set new active state
    this.#currentActiveId = buttonId;
    const activeButton = this.querySelector(`#${buttonId}`);
    if (activeButton) {
      activeButton.classList.add("v24-active-apt-type");
      activeButton.setAttribute("aria-selected", "true");
      this.setAttribute("aria-activedescendant", buttonId);
    }
  }

  setupAppointmentTypeUI(data) {
    this.innerHTML = ""; // Clear existing buttons
    
    data.forEach((appointment) => {
      const buttonEl = document.createElement("button");
      buttonEl.setAttribute("class", "btn v24-apt-type-btn");
      buttonEl.setAttribute("id", "v24AptType_" + appointment.id);
      buttonEl.setAttribute("data-id", appointment.id);
      buttonEl.setAttribute("role", "radio");
      buttonEl.setAttribute("tabindex", "-1");
      buttonEl.setAttribute("aria-selected", "false");
      buttonEl.innerHTML = this.#resourcesService.getFormattedResource(appointment.name, 'SELFSCHEDULER_APPOINTMENT_TYPE', '', appointment.name);
      buttonEl.addEventListener("click", (e) => this.chooseAppointmentType(e));

      this.appendChild(buttonEl);
    });

    // Set initial active descendant
    const firstButton = this.querySelector("button");
    if (firstButton) {
      this.#setActiveDescendant(firstButton.id);
    }
    this.addKeyboardNavigation();
  }

  addKeyboardNavigation() {
    const buttons = this.querySelectorAll('.v24-apt-type-btn');
    buttons.forEach((button, currentIndex) => {
      button.addEventListener('keydown', (event) => {
        const nextIndex = getNextFocusIndex(event, currentIndex, buttons.length);
        if (nextIndex !== null) {
          buttons[nextIndex].focus();
        }
      });
    });
  }

  setSelectedAppointmentTypeButton(selectedAppointmentTypeId) {
    const selectedBtn = this.querySelector(`#v24AptType_${selectedAppointmentTypeId}`);
    if (selectedBtn) {
      this.resetSelectedAppointmentTypeButton();
      selectedBtn.classList.add("v24-selected-apt-type");
      this.#setActiveDescendant(selectedBtn.id);
      return true;
    }
    return false;
  }

  chooseAppointmentType(event) {
    this.resetSelectedAppointmentTypeButton();

    const btn = event.target;
    if (btn) {
      const appointmentTypeId = btn.dataset.id;
      btn.classList.add("v24-selected-apt-type");
      this.#setActiveDescendant(btn.id);
      
      this.dispatchEvent(
        new CustomEvent(
          APPOINTMENT_TYPE_SELECTION_EVENTS.appointmentTypeSelected,
          {
            bubbles: true,
            detail: {
              appointmentTypeId
            }
          }
        )
      );
    }
  }

  resetSelectedAppointmentTypeButton() {
    const prevSelectedBtns = this.querySelectorAll(".v24-selected-apt-type");
    prevSelectedBtns.forEach(btn => {
      btn.classList.remove("v24-selected-apt-type");
    });
  }
}
